import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Linking, Platform, Pressable, ScrollView, StyleSheet, View, type ViewStyle } from "react-native";
import { AnimatePresence, MotiView } from "moti";
import {
  ArrowSquareOutIcon,
  CaretRightIcon,
  CheckCircleIcon,
  CircleIcon,
  ClockCounterClockwiseIcon,
  DownloadSimpleIcon,
  ListChecksIcon,
  TrashIcon,
  XIcon,
} from "@phosphor-icons/react";
import * as Sentry from "@sentry/react-native";
import type { GetModernizedCoursePublishHistoryQuery } from "@axio/graphql/generated/graphql";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";

const SHEET_WIDTH = 460;

type PublishHistoryRecord =
  GetModernizedCoursePublishHistoryQuery["getModernizedCoursePublishHistory"]["records"][number];
type PublishHistoryManualStep = PublishHistoryRecord["manualSteps"][number];

interface PublishHistorySheetProps {
  open: boolean;
  records: PublishHistoryRecord[];
  lmsLabel: string;
  isClearing: boolean;
  onClose: () => void;
  onClearHistory: () => void;
}

function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? `${count} ${singular}` : `${count} ${plural}`;
}

function formatPublishedAt(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getStepKey(recordId: string, stepId: string): string {
  return `${recordId}:${stepId}`;
}

async function openUrl(url: string | null | undefined): Promise<void> {
  if (!url) {
    return;
  }

  try {
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }

    const canOpenUrl = await Linking.canOpenURL(url);
    if (!canOpenUrl) {
      Sentry.captureException(new Error("Unsupported publish history URL"));
      return;
    }

    await Linking.openURL(url);
  } catch (error) {
    Sentry.captureException(error);
  }
}

function ManualStepCard({
  recordId,
  step,
  completed,
  lmsLabel,
  onToggle,
}: {
  recordId: string;
  step: PublishHistoryManualStep;
  completed: boolean;
  lmsLabel: string;
  onToggle: (recordId: string, stepId: string) => void;
}) {
  const hasDownload = Boolean(step.downloadUrl);
  const hasExternalUrl = Boolean(step.externalUrl);

  return (
    <View style={[styles.manualStepCard, completed && styles.manualStepCardComplete]}>
      <Pressable
        onPress={() => onToggle(recordId, step.id)}
        style={styles.stepToggle}
        accessibilityRole="checkbox"
        accessibilityState={{ checked: completed }}
        accessibilityLabel={`${completed ? "Mark incomplete" : "Mark complete"} ${step.title}`}>
        {completed ? (
          <CheckCircleIcon size={16} color={colors.success[500]} weight="regular" />
        ) : (
          <CircleIcon size={16} color={colors.grey[300]} weight="regular" />
        )}
      </Pressable>
      <View style={styles.manualStepContent}>
        <CustomText
          text={step.action}
          useTranslationText={false}
          size="xs"
          weight="medium"
          style={styles.manualStepTitle}
        />
        <CustomText text={step.detail} useTranslationText={false} size="xs" style={styles.manualStepDetail} />
        <View style={styles.manualStepActions}>
          <Pressable
            onPress={() => {
              void openUrl(step.downloadUrl);
            }}
            disabled={!hasDownload}
            style={[styles.actionButton, !hasDownload && styles.actionButtonDisabled]}
            accessibilityLabel={`Download ${step.title}`}>
            <DownloadSimpleIcon size={10} color={colors.grey[600]} weight="regular" />
            <CustomText
              text="Download"
              useTranslationText={false}
              size="xs"
              weight="medium"
              style={styles.actionButtonText}
            />
          </Pressable>
          <Pressable
            onPress={() => {
              void openUrl(step.externalUrl);
            }}
            disabled={!hasExternalUrl}
            style={[styles.openButton, !hasExternalUrl && styles.actionButtonDisabled]}
            accessibilityLabel={`Open destination for ${step.title}`}>
            <ArrowSquareOutIcon size={10} color={colors.grey[600]} weight="regular" />
            <CustomText
              text={`Open in ${lmsLabel}`}
              useTranslationText={false}
              size="xs"
              weight="medium"
              style={styles.openButtonText}
            />
          </Pressable>
        </View>
      </View>
    </View>
  );
}

function PublishHistorySheet({
  open,
  records,
  lmsLabel,
  isClearing,
  onClose,
  onClearHistory,
}: PublishHistorySheetProps) {
  const [expandedRecordId, setExpandedRecordId] = useState<string | null>(null);
  const [completedStepIds, setCompletedStepIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!open) {
      return;
    }

    setExpandedRecordId((current) => {
      if (current && records.some((record) => record.id === current)) {
        return current;
      }
      return records[0]?.id ?? null;
    });
  }, [open, records]);

  const getCompletedManualStepCount = useCallback(
    (record: PublishHistoryRecord) =>
      record.manualSteps.filter((step) => step.completed || completedStepIds.has(getStepKey(record.id, step.id)))
        .length,
    [completedStepIds]
  );

  const pendingManualStepCount = useMemo(
    () =>
      records.reduce((sum, record) => {
        const completedCount = getCompletedManualStepCount(record);
        return sum + Math.max(record.manualStepCount - completedCount, 0);
      }, 0),
    [getCompletedManualStepCount, records]
  );

  const handleToggleManualStep = useCallback((recordId: string, stepId: string) => {
    const key = getStepKey(recordId, stepId);
    setCompletedStepIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const subtitle =
    records.length === 0
      ? "Publish history will appear here after you publish changes."
      : `${pluralize(records.length, "publish", "publishes")} · ${pluralize(
          pendingManualStepCount,
          "manual step",
          "manual steps"
        )} remaining`;

  return (
    <AnimatePresence>
      {open && (
        <MotiView
          key="publish-history-overlay"
          from={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ type: "timing", duration: 150 }}
          style={styles.overlay}>
          <Pressable style={styles.overlayBackdrop} onPress={onClose} />
          <MotiView
            key="publish-history-panel"
            from={{ translateX: SHEET_WIDTH }}
            animate={{ translateX: 0 }}
            exit={{ translateX: SHEET_WIDTH }}
            transition={{ type: "timing", duration: 220 }}
            style={styles.sheet}>
            <View style={styles.header}>
              <View style={styles.headerTop}>
                <CustomText
                  text="Publish History"
                  useTranslationText={false}
                  size="l"
                  weight="bold"
                  style={styles.title}
                />
                <Pressable onPress={onClose} style={styles.closeButton} accessibilityLabel="Close publish history">
                  <XIcon size={18} color={colors.grey[500]} weight="regular" />
                </Pressable>
              </View>
              <CustomText text={subtitle} useTranslationText={false} size="s" style={styles.description} />
            </View>

            {records.length === 0 ? (
              <View style={styles.emptyState}>
                <ClockCounterClockwiseIcon size={32} color={colors.grey[300]} weight="regular" />
                <CustomText
                  text="Publish history will appear here after you publish changes."
                  useTranslationText={false}
                  size="s"
                  style={styles.emptyStateText}
                />
              </View>
            ) : (
              <ScrollView style={styles.scrollArea} contentContainerStyle={styles.scrollContent}>
                {records.map((record) => {
                  const isExpanded = expandedRecordId === record.id;
                  const completedManualStepCount = getCompletedManualStepCount(record);
                  const pendingCount = Math.max(record.manualStepCount - completedManualStepCount, 0);
                  const hasManualSteps = record.manualSteps.length > 0;
                  const recordBodyMaxHeight =
                    180 + record.autoPublishedItems.length * 34 + record.manualSteps.length * 120;

                  return (
                    <View key={record.id} style={styles.historyRecord}>
                      <Pressable
                        onPress={() => setExpandedRecordId(isExpanded ? null : record.id)}
                        style={styles.recordHeader}
                        accessibilityLabel={`${isExpanded ? "Collapse" : "Expand"} publish from ${formatPublishedAt(
                          record.publishedAt
                        )}`}>
                        <View style={styles.recordHeaderText}>
                          <View style={styles.recordTitleRow}>
                            <CustomText
                              text={formatPublishedAt(record.publishedAt)}
                              useTranslationText={false}
                              size="s"
                              weight="medium"
                              style={styles.recordTitle}
                            />
                            {hasManualSteps && (
                              <View style={[styles.statusBadge, pendingCount === 0 && styles.statusBadgeComplete]}>
                                <CustomText
                                  text={pendingCount === 0 ? "complete" : `${pendingCount} pending`}
                                  useTranslationText={false}
                                  size="xs"
                                  weight="medium"
                                  style={[styles.statusBadgeText, pendingCount === 0 && styles.statusBadgeTextComplete]}
                                />
                              </View>
                            )}
                          </View>
                          <CustomText
                            text={`${record.autoPublishedCount} published${
                              hasManualSteps
                                ? ` · ${completedManualStepCount}/${record.manualStepCount} manual steps done`
                                : ""
                            }`}
                            useTranslationText={false}
                            size="xs"
                            style={styles.recordMeta}
                          />
                        </View>
                        <MotiView
                          animate={{ rotate: isExpanded ? "90deg" : "0deg" }}
                          transition={{ type: "timing", duration: 280 }}>
                          <CaretRightIcon size={16} color={colors.grey[500]} weight="bold" />
                        </MotiView>
                      </Pressable>

                      <AnimatePresence>
                        {isExpanded && (
                          <MotiView
                            key={`${record.id}-body`}
                            from={{ maxHeight: 0, opacity: 0 }}
                            animate={{ maxHeight: recordBodyMaxHeight, opacity: 1 }}
                            exit={{ maxHeight: 0, opacity: 0 }}
                            transition={{ type: "timing", duration: 340 }}
                            style={styles.recordBodyAnimation}>
                            <View style={styles.recordBody}>
                              {record.autoPublishedItems.length > 0 && (
                                <View style={styles.recordSection}>
                                  <View style={styles.sectionHeader}>
                                    <CheckCircleIcon size={16} color={colors.success[700]} weight="regular" />
                                    <CustomText
                                      text={`Published to ${lmsLabel}`}
                                      useTranslationText={false}
                                      size="xs"
                                      weight="bold"
                                      style={styles.publishedSectionTitle}
                                    />
                                  </View>
                                  <View style={styles.publishedList}>
                                    {record.autoPublishedItems.map((item) => (
                                      <View key={item.id} style={styles.publishedItem}>
                                        <CheckCircleIcon size={16} color={colors.success[500]} weight="regular" />
                                        <View style={styles.publishedItemText}>
                                          <CustomText
                                            text={item.title}
                                            useTranslationText={false}
                                            size="xs"
                                            style={styles.publishedItemTitle}
                                            numberOfLines={1}
                                          />
                                          <CustomText
                                            text={item.category}
                                            useTranslationText={false}
                                            size="xs"
                                            style={styles.publishedItemMeta}
                                            numberOfLines={1}
                                          />
                                        </View>
                                      </View>
                                    ))}
                                  </View>
                                </View>
                              )}

                              {hasManualSteps && (
                                <View style={styles.recordSection}>
                                  <View style={styles.sectionHeader}>
                                    <ListChecksIcon size={12} color={colors.warning[600]} weight="regular" />
                                    <CustomText
                                      text="Manual Steps"
                                      useTranslationText={false}
                                      size="xs"
                                      weight="bold"
                                      style={styles.manualSectionTitle}
                                    />
                                  </View>
                                  <View style={styles.manualSteps}>
                                    {record.manualSteps.map((step) => (
                                      <ManualStepCard
                                        key={step.id}
                                        recordId={record.id}
                                        step={step}
                                        lmsLabel={lmsLabel}
                                        completed={
                                          step.completed || completedStepIds.has(getStepKey(record.id, step.id))
                                        }
                                        onToggle={handleToggleManualStep}
                                      />
                                    ))}
                                  </View>
                                </View>
                              )}
                            </View>
                          </MotiView>
                        )}
                      </AnimatePresence>
                    </View>
                  );
                })}
              </ScrollView>
            )}

            <View style={styles.footer}>
              <Pressable
                onPress={onClearHistory}
                disabled={records.length === 0 || isClearing}
                style={[styles.clearButton, (records.length === 0 || isClearing) && styles.clearButtonDisabled]}
                accessibilityLabel="Clear publish history">
                <TrashIcon size={14} color={colors.grey[500]} weight="regular" />
                <CustomText
                  text={isClearing ? "Clearing..." : "Clear History"}
                  useTranslationText={false}
                  size="xs"
                  weight="medium"
                  style={styles.clearButtonText}
                />
              </Pressable>
              <Pressable onPress={onClose} style={styles.doneButton} accessibilityLabel="Close publish history">
                <CustomText
                  text="Close"
                  useTranslationText={false}
                  size="s"
                  weight="medium"
                  style={styles.doneButtonText}
                />
              </Pressable>
            </View>
          </MotiView>
        </MotiView>
      )}
    </AnimatePresence>
  );
}

const styles = StyleSheet.create({
  overlay: {
    ...(Platform.OS === "web"
      ? ({
          position: "fixed" as unknown as "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          zIndex: 1000,
        } as ViewStyle)
      : {
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        }),
  },
  overlayBackdrop: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: "rgba(255, 255, 255, 0.02)",
  },
  sheet: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    width: SHEET_WIDTH,
    maxWidth: "100%",
    backgroundColor: colors.white,
    borderLeftWidth: 1,
    borderLeftColor: colors.grey[200],
    flexDirection: "column",
    ...(Platform.OS === "web"
      ? ({
          boxShadow: "-8px 0 24px rgba(16, 24, 40, 0.06)",
        } as unknown as ViewStyle)
      : {
          shadowColor: "#101828",
          shadowOpacity: 0.06,
          shadowRadius: 24,
          shadowOffset: { width: -8, height: 0 },
        }),
  },
  header: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 16,
    gap: 8,
  },
  headerTop: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 12,
  },
  title: {
    color: colors.grey[900],
    flex: 1,
  },
  closeButton: {
    padding: 4,
    borderRadius: 6,
  },
  description: {
    color: colors.grey[700],
    lineHeight: 20,
  },
  emptyState: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 56,
    gap: 12,
  },
  emptyStateText: {
    color: colors.grey[500],
    textAlign: "center",
    lineHeight: 20,
  },
  scrollArea: {
    flex: 1,
    minHeight: 0,
  },
  scrollContent: {
    paddingBottom: 8,
  },
  historyRecord: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
  },
  recordHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 24,
    paddingVertical: 12,
    gap: 12,
  },
  recordHeaderText: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  recordTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  recordTitle: {
    color: colors.grey[900],
    lineHeight: 20,
  },
  recordMeta: {
    color: colors.grey[500],
    lineHeight: 16,
  },
  statusBadge: {
    borderRadius: 999,
    backgroundColor: colors.warning[100],
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  statusBadgeComplete: {
    backgroundColor: colors.success[50],
  },
  statusBadgeText: {
    color: colors.warning[700],
    fontSize: 10,
  },
  statusBadgeTextComplete: {
    color: colors.success[700],
  },
  recordBodyAnimation: {
    overflow: "hidden",
  },
  recordBody: {
    paddingHorizontal: 24,
    paddingBottom: 16,
    gap: 12,
  },
  recordSection: {
    gap: 6,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  publishedSectionTitle: {
    color: colors.success[700],
    fontSize: 11,
    lineHeight: 16,
  },
  manualSectionTitle: {
    color: colors.warning[700],
    fontSize: 11,
    lineHeight: 16,
  },
  publishedList: {
    gap: 4,
  },
  publishedItem: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
  },
  publishedItemText: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  publishedItemTitle: {
    color: colors.grey[800],
    flexShrink: 1,
    minWidth: 0,
  },
  publishedItemMeta: {
    color: colors.grey[500],
    lineHeight: 16,
    flexShrink: 0,
  },
  manualSteps: {
    gap: 8,
  },
  manualStepCard: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    borderWidth: 1,
    borderColor: "rgb(234, 236, 240)",
    backgroundColor: colors.warning[50],
    borderRadius: 16,
    padding: 10,
  },
  manualStepCardComplete: {
    backgroundColor: colors.success[25],
    borderColor: colors.success[100],
  },
  stepToggle: {
    paddingTop: 2,
  },
  manualStepContent: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  manualStepTitle: {
    color: colors.grey[900],
    lineHeight: 16,
  },
  manualStepDetail: {
    color: colors.grey[500],
    fontSize: 11,
    lineHeight: 16,
  },
  manualStepActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingTop: 6,
    flexWrap: "wrap",
  },
  actionButton: {
    height: 24,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
    borderRadius: 6,
    paddingHorizontal: 10,
  },
  openButton: {
    height: 24,
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 4,
  },
  actionButtonDisabled: {
    opacity: 0.45,
  },
  actionButtonText: {
    color: colors.grey[700],
    fontSize: 11,
    lineHeight: 14,
  },
  openButtonText: {
    color: colors.grey[700],
    fontSize: 11,
    lineHeight: 14,
  },
  footer: {
    minHeight: 64,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
  },
  clearButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingVertical: 8,
    paddingRight: 8,
  },
  clearButtonDisabled: {
    opacity: 0.45,
  },
  clearButtonText: {
    color: colors.grey[500],
  },
  doneButton: {
    minWidth: 76,
    height: 36,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
  },
  doneButtonText: {
    color: colors.grey[700],
  },
});

export default PublishHistorySheet;
